import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useMe } from '../lib/auth';
import { useRoomStore } from '../store/roomStore';
import { BrandMark } from './BrandMark';
import { UserMenu } from './UserMenu';

/** Nav lives in one place so the desktop rail and the mobile strip never drift apart. `Admin` is
 * appended only for an ADMIN session — `useMe()` is already warm by the time this renders (this
 * shell only ever mounts inside `RequireAuth`), so no extra loading state to handle here. */
const BASE_NAV = [{ to: '/', label: 'Rooms', index: '01' }] as const;
const ADMIN_NAV_ITEM = { to: '/admin/rooms', label: 'Admin', index: '02' } as const;
const ADMIN_TEMPLATES_NAV_ITEM = { to: '/admin/templates', label: 'Templates', index: '03' } as const;

function useNav() {
  const { data: me } = useMe();
  return me?.role === 'ADMIN' ? [...BASE_NAV, ADMIN_NAV_ITEM, ADMIN_TEMPLATES_NAV_ITEM] : BASE_NAV;
}

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    'group flex min-h-tap items-center gap-3 border-l-2 px-4 transition-colors md:flex-col md:gap-1 md:border-l-0 md:border-t-2 md:px-0 md:py-3',
    isActive
      ? 'border-accent text-text'
      : 'border-transparent text-text-muted hover:border-border-strong hover:text-text',
  ].join(' ');
}

/** The header wordmark slot: the room title while on `/rooms/:id` and its snapshot has loaded
 * into the store, the app name everywhere else. */
function HeaderTitle() {
  const location = useLocation();
  const snapshot = useRoomStore((state) => state.snapshot);
  const match = /^\/rooms\/(\d+)/.exec(location.pathname);
  const roomId = match?.[1];

  if (roomId !== undefined && snapshot && String(snapshot.room.id) === roomId) {
    return <span className="display truncate text-lg tracking-wide text-text-muted">{snapshot.room.title}</span>;
  }
  return <span className="display text-lg tracking-wide text-text-muted">Strylander Interview Room</span>;
}

/**
 * Broadcast-switcher layout: a narrow vertical rail on the left (monogram, numbered nav, the
 * signed-in user at the foot) and a wide stage on the right. Below `md` the rail collapses into a
 * top strip, and the session block moves to the strip's right end so tap targets stay >=44px.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const nav = useNav();
  return (
    <div className="relative flex min-h-full flex-col bg-bg text-text md:flex-row">
      <div className="atmosphere" />

      <aside className="relative z-10 flex items-center gap-2 border-b border-border bg-bg/80 px-3 backdrop-blur-sm md:sticky md:top-0 md:h-screen md:w-rail md:flex-col md:items-stretch md:gap-0 md:border-r md:border-b-0 md:px-0">
        <a
          href="/"
          className="flex min-h-tap items-center gap-3 md:justify-center md:px-0 md:py-4"
          aria-label="Strylander Interview Room home"
        >
          <BrandMark size={36} />
        </a>

        <nav aria-label="Main navigation" className="flex items-stretch md:flex-1 md:flex-col">
          {nav.map((item, i) => (
            <NavLink key={item.to} to={item.to} end className={navClass} style={{ ['--i' as string]: i + 1 }}>
              <span className="eyebrow hidden md:block" aria-hidden="true">
                {item.index}
              </span>
              <span className="text-sm font-semibold tracking-wide">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Session block: the "on-air" tally + the signed-in user. Right end of the mobile strip,
            the rail's foot on desktop. */}
        <div className="ml-auto flex items-center gap-3 md:ml-0 md:w-full md:flex-col md:items-stretch md:gap-3 md:border-t md:border-border md:px-3 md:py-4">
          <span
            className="block size-2 shrink-0 rounded-full bg-signal blink"
            aria-hidden="true"
            title="Session live"
          />
          <UserMenu />
        </div>
      </aside>

      <div className="relative z-10 flex flex-1 flex-col">
        <header className="flex min-h-tap items-center justify-between gap-4 px-5 md:px-10">
          <HeaderTitle />
          <span className="eyebrow hidden sm:block">Class Interview Series</span>
        </header>
        <main className="flex-1 px-5 pb-16 pt-6 md:px-10 md:pt-10">{children}</main>
      </div>
    </div>
  );
}
